import React from 'react';

import FormModal from '../../../Modals/FormModal';

import InputField from '../../../Forms/Input/InputField';

import { useSale } from '../../../../hooks/sale/useSale';
import { DOCUMENT_TYPE_CODES } from '../../../../constants/documentTypeCodes';
import SelectField from '../../../Forms/Input/SelectField';

interface Props {
  isOpen: boolean;

  onClose: () => void;

  onSave: (updated: {
    detailId: number;
    productId: number;
    quantity: number;
    unitPrice: number;
    discountPercentage: number;
    taxType: 'TAXED' | 'EXEMPT' | 'NON_SUBJECT';
  }) => void;
}

const POSUpdateSelectedProductModal: React.FC<Props> = ({
  isOpen,
  onClose,
  onSave,
}) => {
  // =====================================================
  // 🔹 Hooks
  // =====================================================
  const {
    form,

    selectedProduct,

    handlerUpdateSelectedProductField,
  } = useSale();

  const isCreditNote =
    form?.documentType?.code === DOCUMENT_TYPE_CODES.CREDIT_NOTE;

  // =====================================================
  // 🔹 Guards
  // =====================================================
  if (!selectedProduct) {
    return null;
  }

  const discountDisabled = selectedProduct.appliesDiscount === false;

  // =====================================================
  // 🔹 Handlers
  // =====================================================
  const handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (value === '') {
      handlerUpdateSelectedProductField({
        quantity: undefined,
      });

      return;
    }

    const parsed = Number(value);

    if (!isNaN(parsed) && parsed >= 0) {
      handlerUpdateSelectedProductField({
        quantity: parsed,
      });
    }
  };

  const handleUnitPriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (value === '') {
      handlerUpdateSelectedProductField({
        unitPrice: undefined,
      });

      return;
    }

    const parsed = Number(value);

    if (!isNaN(parsed) && parsed >= 0) {
      handlerUpdateSelectedProductField({
        unitPrice: parsed,
      });
    }
  };

  const handleSubmit = () => {
    onSave({
      detailId: selectedProduct.saleDetailId,

      productId: selectedProduct.productId,

      quantity: Number(selectedProduct.quantity),

      unitPrice: Number(selectedProduct.unitPrice),

      discountPercentage: Number(selectedProduct.discountPercentage ?? 0),

      taxType: selectedProduct.taxType ?? 'TAXED',

      creditFiscalId: selectedProduct.creditFiscalId,

      creditFiscalDetailId: selectedProduct.creditFiscalDetailId,
    });

    onClose();
  };

  // =====================================================
  // 🎨 Render
  // =====================================================
  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title="Editar producto"
      maxWidth="max-w-2xl"
      overlayBg="black"
    >
      <div className="flex flex-col gap-5">
        {/* ===================================================== */}
        {/* 🔹 Product */}
        {/* ===================================================== */}
        <div>
          <h3
            className="
              text-sm
              font-semibold
              text-black

              dark:text-white
            "
          >
            {selectedProduct.productName}
          </h3>
        </div>

        {/* ===================================================== */}
        {/* 🔹 Fields */}
        {/* ===================================================== */}
        <div
          className="
          grid
          grid-cols-1
          gap-4

          md:grid-cols-2
        "
        >
          <SelectField
            label="Tipo Tributario"
            name="taxType"
            value={selectedProduct.taxType ?? 'TAXED'}
            disabled
            options={[
              {
                value: 'TAXED',
                label: 'Gravado',
              },
              {
                value: 'EXEMPT',
                label: 'Exento',
              },
              {
                value: 'NON_SUBJECT',
                label: 'No sujeto',
              },
            ]}
            iconKey="document"
            onChange={() => {}}
          />

          <InputField
            label="Descuento (%)"
            name="discountPercentage"
            type="number"
            step="0.01"
            min={0}
            max={100}
            iconKey="discount"
            disabled={discountDisabled}
            value={
              discountDisabled
                ? ''
                : selectedProduct.discountPercentage === undefined
                ? ''
                : selectedProduct.discountPercentage
            }
            onChange={(e) => {
              const value = e.target.value;

              if (value === '') {
                handlerUpdateSelectedProductField({
                  discountPercentage: undefined,
                });

                return;
              }

              const parsed = Number(value);

              if (!isNaN(parsed) && parsed >= 0) {
                handlerUpdateSelectedProductField({
                  discountPercentage: parsed,
                });
              }
            }}
          />

          <InputField
            label="Cantidad"
            name="quantity"
            type="number"
            min={0}
            iconKey="hash"
            value={
              selectedProduct.quantity === 0 ? '' : selectedProduct.quantity
            }
            onChange={handleQuantityChange}
          />

          <InputField
            label="Precio"
            name="unitPrice"
            type="number"
            step="0.01"
            min={0}
            iconKey="dollar"
            disabled={isCreditNote}
            value={
              selectedProduct.unitPrice === 0 ? '' : selectedProduct.unitPrice
            }
            onChange={handleUnitPriceChange}
          />
        </div>

        {discountDisabled && (
          <p className="-mt-3 text-xs text-bodydark2">
            Este producto no admite descuentos.
          </p>
        )}

        {/* ===================================================== */}
        {/* 🔹 Total */}
        {/* ===================================================== */}
        <div
          className="
            text-right
            text-sm
            font-semibold
            text-primary
          "
        >
          Total: $
          {(
            (selectedProduct.quantity ?? 0) * (selectedProduct.unitPrice ?? 0)
          ).toFixed(2)}
        </div>

        {/* ===================================================== */}
        {/* 🔹 Actions */}
        {/* ===================================================== */}
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="
              rounded-lg
              border
              border-stroke

              px-4
              py-2

              text-sm
              font-medium

              transition-all

              hover:bg-whiten

              dark:border-strokedark
              dark:hover:bg-meta-4
            "
          >
            Cancelar
          </button>

          <button
            onClick={handleSubmit}
            className="
              rounded-lg

              bg-primary

              px-4
              py-2

              text-sm
              font-medium
              text-white

              transition-all

              hover:bg-opacity-90
            "
          >
            Guardar
          </button>
        </div>
      </div>
    </FormModal>
  );
};

export default POSUpdateSelectedProductModal;
